// mc-loader.js — loader for the Multiple Choice test data layer (Brief 057).
//
// content/mc-tests.json is an itemId → test-descriptor map extracted from the
// iOS plist corpus by tools/extract-mc-tests.mjs. Brief 058's MC engine + UI
// consult it to present the test: title, pass score, sub-type, and the ordered
// questions (each with answer choices + which choice is correct).
//
// Loaded once at boot via loadMcTests(); getMcTest() then queries it
// synchronously. Mirrors meter-rules-loader.js (Brief 054).
//
// Field notes for consumers (Brief 058):
//   - prompt / choice text use a { en, zh } shape, but the corpus only carries
//     one language: the content lives in `.en` (often Chinese for the
//     Method/Metaphor/Third-Ear tests) and `.zh` is empty. iOS renders the
//     `.en` slot unconditionally — mirror that; do NOT pick by UI language.
//   - correctIndex is the 0-based index of the correct choice; each choice also
//     carries a `correct` boolean. The corpus has exactly one correct choice
//     per question (correctIndex === -1 would mean malformed data).
//   - mcType is the raw plist integer (1/2/3); subType is its label
//     ("Third Ear Comprehension" / "Method" / "Metaphor"), or null if the
//     integer is unmapped.

const CONTENT_BASE = "/content";

/**
 * @typedef {object} McChoice
 * @property {{ en: string, zh: string }} text
 * @property {boolean} correct
 *
 * @typedef {object} McQuestion
 * @property {{ en: string, zh: string }} prompt
 * @property {McChoice[]} choices
 * @property {number} correctIndex 0-based index of the correct choice; -1 if none.
 *
 * @typedef {object} McTest
 * @property {string} titleEn
 * @property {string} titleZh
 * @property {string} category       CMS category letter (e.g. "H").
 * @property {number|null} passScore pass threshold as a percentage (e.g. 50).
 * @property {string|null} subType   "Third Ear Comprehension" | "Method" | "Metaphor" | null.
 * @property {number|null} mcType    raw plist MCType integer.
 * @property {McQuestion[]} questions
 */

/** @type {Record<string, McTest> | null} */
let cache = null;
/** @type {Promise<void> | null} */
let promise = null;

/**
 * Synchronously inject the mc-tests map (test seam / non-fetch boot).
 * @param {Record<string, McTest> | null} m
 */
export function setMcTests(m) {
  cache = m;
  promise = Promise.resolve();
}

/**
 * Fetch content/mc-tests.json once and cache it. Safe to call repeatedly —
 * subsequent calls return the same promise. If the file is missing or fails to
 * parse, the cache is set to {} and getMcTest() returns null for every item.
 * @returns {Promise<void>}
 */
export function loadMcTests() {
  if (promise) return promise;
  promise = fetch(`${CONTENT_BASE}/mc-tests.json`)
    .then((r) => (r.ok ? r.json() : {}))
    .catch(() => ({}))
    .then((j) => {
      cache = (j && typeof j === "object")
        ? /** @type {Record<string, McTest>} */ (j)
        : {};
    });
  return promise;
}

/**
 * Synchronous lookup of a single MC test descriptor. Returns the record or null
 * if the item is not an MC test (or has no descriptor). Accepts numeric or
 * string ids.
 * @param {number|string} itemId
 * @returns {McTest | null}
 */
export function getMcTest(itemId) {
  if (!cache) throw new Error("[mc-tests] not loaded — call loadMcTests() first");
  return cache[String(itemId)] ?? null;
}
